import { useState, useEffect } from "react";
import { Home, Briefcase, Users, Phone, Calendar, Menu, X, MapPin, MessageCircle, Wrench, Camera, Lightbulb, Zap, TrendingUp, Target, Video, Euro, FileText, ChevronRight, Grid3x3, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo-small.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookingDialog } from "./BookingDialogLazy";
import { trackWhatsAppClick, trackPhoneClick, trackEmailClick } from "@/lib/analytics";
import { useNavigate, useLocation, Link } from "@/lib/router-compat";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useIndustries } from "@/hooks/useIndustries";
import { useProjects } from "@/hooks/useProjects";
import { servicesData } from "@/data/servicesData";

const PHONE_HREF = "tel:+4917670027200";
const WHATSAPP_URL = `https://wa.me/4917670027200?text=${encodeURIComponent("Hallo Clicks DMP, ich interessiere mich für Ihre Services!")}`;

// Home page sections
const homeNavItems = [
  { type: "section", id: "hero", label: "Home", icon: Home },
  { type: "section", id: "philosophy", label: "Über uns", icon: Users },
  { type: "section", id: "services", label: "Unsere Services", icon: Wrench },
  { type: "section", id: "service-areas", label: "Servicebereich", icon: MapPin },
  { type: "section", id: "industries", label: "Branchen", icon: Briefcase },
  { type: "section", id: "portfolio", label: "Portfolio", icon: Grid3x3 },
];

// Social media page sections
const socialMediaNavItems = [
  { type: "section", id: "problem", label: "Herausforderungen", icon: Camera },
  { type: "section", id: "impact", label: "Impact", icon: TrendingUp },
  { type: "section", id: "solutions", label: "Lösungen", icon: Lightbulb },
  { type: "section", id: "why-us", label: "Warum wir", icon: Target },
  { type: "section", id: "services", label: "Leistungen", icon: Wrench },
  { type: "section", id: "portfolio", label: "Portfolio", icon: Video },
  { type: "section", id: "pricing", label: "Pakete", icon: Euro },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { data: industries = [] } = useIndustries();
  const { data: allProjects = [] } = useProjects();
  const [isVisible, setIsVisible] = useState(true);
  const [activeSection, setActiveSection] = useState("hero");
  const [showBooking, setShowBooking] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showGlow, setShowGlow] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showHeader, setShowHeader] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(true);
  const [sectionsOpen, setSectionsOpen] = useState(true);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  
  // Get page name for breadcrumb
  const getPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'Startseite';
    if (path === '/portfolio') return 'Portfolio';
    if (path.startsWith('/portfolio/')) return 'Projekt Details';
    if (path === '/socialmedia') return 'Social Media';
    if (path === '/blog') return 'Blog';
    if (path.startsWith('/blog/')) return 'Blog Artikel';
    if (path === '/termin') return 'Termin buchen';
    if (path === '/impressum') return 'Impressum';
    if (path === '/datenschutz') return 'Datenschutz';
    if (path === '/agb') return 'AGB';
    return 'Seite';
  };
  
  // Get current section navigation based on page
  const getCurrentSections = () => {
    if (location.pathname === '/socialmedia') return socialMediaNavItems;
    if (location.pathname === '/') return homeNavItems;
    return [];
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentSections = getCurrentSections();
      const sections = currentSections.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + window.innerHeight / 3;
      const currentScrollY = window.scrollY;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const navItem = currentSections[i];
        if (section && navItem && section.offsetTop <= scrollPosition) {
          setActiveSection(navItem.id);
          break;
        }
      }

      setScrolled(currentScrollY > 20);
      
      // Show glow only when near the top of the page
      setShowGlow(currentScrollY < 100);
      
      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowHeader(false);
      } else if (currentScrollY < lastScrollY) {
        setShowHeader(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, location.pathname]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMenuOpen(false);
    }
  };

  const navigateToPage = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleWhatsApp = () => {
    trackWhatsAppClick('header');
    setMenuOpen(false);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    trackPhoneClick();
    if (!isMobile) {
      e.preventDefault();
      toast({
        title: "Telefonnummer",
        description: "+49 176 70027200",
        duration: 5000,
      });
    }
  };

  const handleNavItemClick = (item: any) => {
    if (item.type === 'section') {
      // If we're not on the right page, navigate there first
      if (location.pathname === '/socialmedia' && !socialMediaNavItems.find(i => i.id === item.id)) {
        navigate('/');
        setTimeout(() => scrollToSection(item.id), 100);
      } else if (location.pathname === '/' && !homeNavItems.find(i => i.id === item.id)) {
        navigate('/socialmedia');
        setTimeout(() => scrollToSection(item.id), 100);
      } else {
        scrollToSection(item.id);
      }
    } else if (item.type === 'page') {
      navigateToPage(item.path);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      scrollToSection('hero');
    }
  };

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out",
          isVisible && showHeader ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        )}
      >
        <div
          className={cn(
            "mx-4 md:mx-8 mt-4 rounded-2xl border transition-all duration-500",
            scrolled 
              ? "bg-background/95 backdrop-blur-xl border-primary/30 shadow-2xl" 
              : "bg-background/60 backdrop-blur-md border-primary/20"
          )}
          style={{
            boxShadow: scrolled 
              ? "0 10px 40px -10px hsl(var(--primary) / 0.4), 0 0 80px hsl(var(--primary) / 0.15)"
              : "0 8px 32px -8px hsl(var(--primary) / 0.2)"
          }}
        >
          <div className="container-custom mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between h-16 md:h-20">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" onClick={handleLogoClick} className="relative group cursor-pointer" aria-label="Clicks DMP – zur Startseite">
                  <div className="absolute inset-0 blur-lg opacity-20 bg-gradient-to-r from-primary/30 via-primary/15 to-primary/30 group-hover:opacity-40 transition-opacity" />
                  <img
                    src={logo}
                    alt="Clicks DMP Logo"
                    className="relative h-7 md:h-8 w-auto hover:scale-105 transition-transform duration-500"
                    style={{
                      filter: "drop-shadow(0 2px 8px hsl(var(--primary) / 0.2))",
                    }}
                  />
                </Link>
              </div>

              {/* Desktop & Mobile Actions */}
              <div className="flex items-center gap-2">
                {/* Phone Button */}
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 hover:bg-primary/10 hover:text-primary transition-all"
                >
                  <a href={PHONE_HREF} onClick={handleContactClick} aria-label="Anrufen: +49 176 70027200">
                    <Phone className="w-5 h-5" />
                    <span className="hidden lg:inline text-sm">Telefon</span>
                  </a>
                </Button>

                {/* WhatsApp Button */}
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 hover:bg-primary/10 hover:text-primary transition-all"
                >
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener" onClick={handleWhatsApp} aria-label="Per WhatsApp schreiben">
                    <MessageCircle className="w-5 h-5" />
                    <span className="hidden lg:inline text-sm">WhatsApp</span>
                  </a>
                </Button>

                {/* Booking Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBooking(true)}
                  className="flex items-center gap-2 hover:bg-primary/10 hover:text-primary transition-all"
                  aria-label="Termin buchen"
                >
                  <Calendar className="w-5 h-5" />
                  <span className="hidden lg:inline text-sm">Termin buchen</span>
                </Button>

                {/* Hamburger Menu */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="ml-2"
                  aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
                >
                  {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={cn(
            "absolute top-full left-4 right-4 mt-2 bg-background/98 backdrop-blur-xl border border-border rounded-2xl shadow-2xl transition-all duration-300 overflow-hidden z-40",
            menuOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <nav className="px-4 py-4 overflow-y-auto max-h-[75vh]">
            {/* Breadcrumb */}
            <div className="mb-4 pb-4 border-b border-border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                <Link to="/" onClick={() => setMenuOpen(false)} aria-label="Startseite">
                  <Home className="w-3 h-3" />
                </Link>
                <span>Sie sind hier:</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={location.pathname} onClick={() => setMenuOpen(false)}>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                    {getPageName()}
                  </Badge>
                </Link>
                {getCurrentSections().length > 0 && activeSection && (
                  <>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    <Badge variant="outline">
                      {getCurrentSections().find(s => s.id === activeSection)?.label || activeSection}
                    </Badge>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              {/* Startseite with sections */}
              <Collapsible>
                <div
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-left group",
                    "hover:bg-primary/10",
                    location.pathname === '/' && "bg-primary/20 text-primary font-medium"
                  )}
                >
                  <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 flex-1">
                    <Home className={cn("w-5 h-5", location.pathname === '/' && "text-primary")} />
                    <span className="flex-1">Startseite</span>
                    {location.pathname === '/' && <Badge variant="secondary" className="text-xs mr-2">Aktiv</Badge>}
                  </Link>
                  <CollapsibleTrigger className="group" aria-label="Abschnitte der Startseite ein- oder ausklappen">
                    <ChevronDown className="w-4 h-4 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent className="ml-12 mt-1">
                  <div className="flex flex-col gap-1">
                    {homeNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id && location.pathname === '/';
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavItemClick(item)}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm",
                            "hover:bg-primary/10",
                            isActive && "bg-primary/10 text-primary"
                          )}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Dienstleistungen with Fotografie, Videografie, Social Media */}
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-left group hover:bg-primary/10">
                  <Wrench className="w-5 h-5" />
                  <span className="flex-1">Dienstleistungen</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
                </CollapsibleTrigger>
                <CollapsibleContent forceMount className="ml-12 mt-1 data-[state=closed]:hidden">
                  <div className="flex flex-col gap-1">
                    {/* Fotografie */}
                    <Link
                      to="/foto"
                      onClick={() => setMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm hover:bg-primary/10",
                        location.pathname === '/foto' && "bg-primary/10 text-primary"
                      )}
                    >
                      <Camera className="w-4 h-4" />
                      <span>Fotografie</span>
                    </Link>
                    
                    {/* Videografie */}
                    <Link
                      to="/film"
                      onClick={() => setMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm hover:bg-primary/10",
                        location.pathname === '/film' && "bg-primary/10 text-primary"
                      )}
                    >
                      <Video className="w-4 h-4" />
                      <span>Videografie</span>
                    </Link>
                    
                    {/* Social Media with its sections */}
                    <Collapsible>
                      <div
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm w-full group",
                          "hover:bg-primary/10",
                          location.pathname === '/socialmedia' && "bg-primary/10 text-primary"
                        )}
                      >
                        <Link to="/socialmedia" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 flex-1">
                          <MessageCircle className="w-4 h-4" />
                          <span className="flex-1">Social Media</span>
                        </Link>
                        <CollapsibleTrigger className="group" aria-label="Abschnitte der Social-Media-Seite ein- oder ausklappen">
                          <ChevronDown className="w-3 h-3 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent className="ml-6 mt-1">
                        <div className="flex flex-col gap-1">
                          {socialMediaNavItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeSection === item.id && location.pathname === '/socialmedia';
                            return (
                              <button
                                key={item.id}
                                onClick={() => handleNavItemClick(item)}
                                className={cn(
                                  "flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-xs",
                                  "hover:bg-primary/10",
                                  isActive && "bg-primary/10 text-primary"
                                )}
                              >
                                <Icon className="w-3 h-3" />
                                <span>{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Portfolio with hierarchy */}
              <Collapsible>
                <div
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-left group",
                    "hover:bg-primary/10",
                    location.pathname.startsWith('/portfolio') && "bg-primary/20 text-primary font-medium"
                  )}
                >
                  <Link to="/portfolio" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 flex-1">
                    <Grid3x3 className={cn("w-5 h-5", location.pathname.startsWith('/portfolio') && "text-primary")} />
                    <span className="flex-1">Portfolio</span>
                    {location.pathname.startsWith('/portfolio') && <Badge variant="secondary" className="text-xs mr-2">Aktiv</Badge>}
                  </Link>
                  <CollapsibleTrigger className="group" aria-label="Portfolio-Branchen ein- oder ausklappen">
                    <ChevronDown className="w-4 h-4 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent className="ml-12 mt-1">
                  <div className="flex flex-col gap-1">
                    {industries.map((industry) => {
                      const industryProjects = allProjects.filter(p => p.industry === industry.name);
                      return (
                        <Collapsible key={industry.name}>
                          <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-primary/10 transition-colors text-left group text-sm">
                            <Briefcase className="w-4 h-4 text-muted-foreground" />
                            <span className="flex-1">{industry.name}</span>
                            <Badge variant="secondary" className="text-xs mr-1">{industryProjects.length}</Badge>
                            <ChevronDown className="w-3 h-3 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
                          </CollapsibleTrigger>
                          <CollapsibleContent className="ml-8 mt-1">
                            {Object.entries(servicesData).map(([serviceKey]) => {
                              const serviceProjects = industryProjects.filter(p => p.category === serviceKey);
                              if (serviceProjects.length === 0) return null;
                              return (
                                <Collapsible key={serviceKey}>
                                  <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-primary/10 transition-colors text-left group text-xs">
                                    <Wrench className="w-3 h-3 text-muted-foreground" />
                                    <span className="flex-1 capitalize">{serviceKey.replace('-', ' ')}</span>
                                    <Badge variant="outline" className="text-xs mr-1">{serviceProjects.length}</Badge>
                                    <ChevronDown className="w-3 h-3 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="ml-6 mt-1">
                                     {serviceProjects.map((project) => (
                                      <Link
                                        key={project.id}
                                        to={`/portfolio/${project.slug}`}
                                        onClick={() => setMenuOpen(false)}
                                        className="flex items-center gap-2 w-full px-3 py-2 text-xs text-left hover:bg-primary/10 rounded-lg transition-colors text-muted-foreground"
                                      >
                                        <ChevronRight className="w-3 h-3" />
                                        <span className="line-clamp-1">{project.title}</span>
                                      </Link>
                                    ))}
                                  </CollapsibleContent>
                                </Collapsible>
                              );
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Blog */}
              <Link
                to="/blog"
                onClick={() => setMenuOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-left",
                  "hover:bg-primary/10",
                  location.pathname === '/blog' && "bg-primary/20 text-primary font-medium"
                )}
              >
                <FileText className={cn("w-5 h-5", location.pathname === '/blog' && "text-primary")} />
                <span className="flex-1">Blog</span>
                {location.pathname === '/blog' && <Badge variant="secondary" className="text-xs">Aktiv</Badge>}
              </Link>

              {/* Kontakt aufnehmen - with contact options */}
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-left group hover:bg-primary/10">
                  <Phone className="w-5 h-5" />
                  <span className="flex-1">Kontakt aufnehmen</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
                </CollapsibleTrigger>
                <CollapsibleContent forceMount className="ml-12 mt-1 data-[state=closed]:hidden">
                  <div className="flex flex-col gap-1">
                    {/* Phone */}
                    <a
                      href={PHONE_HREF}
                      onClick={handleContactClick}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm hover:bg-primary/10"
                    >
                      <Phone className="w-4 h-4" />
                      <span>Telefon</span>
                    </a>
                    
                    {/* WhatsApp */}
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener"
                      onClick={handleWhatsApp}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm hover:bg-primary/10"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>WhatsApp</span>
                    </a>
                    
                    {/* Email */}
                    <a
                      href="mailto:info@clicksdmp.com"
                      onClick={() => { trackEmailClick(); setMenuOpen(false); }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm hover:bg-primary/10"
                    >
                      <FileText className="w-4 h-4" />
                      <span>E-Mail</span>
                    </a>
                    
                    {/* Termin buchen */}
                    <button
                      onClick={() => {
                        setShowBooking(true);
                        setMenuOpen(false);
                      }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm hover:bg-primary/10"
                    >
                      <Calendar className="w-4 h-4" />
                      <span>Termin buchen</span>
                    </button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </nav>
        </div>

        {/* Neon underglow effect - Multiple layers for realistic glow */}
        <div className={cn(
          "absolute -bottom-1 left-0 right-0 h-[4px] pointer-events-none transition-opacity duration-700",
          showGlow ? "opacity-100" : "opacity-0"
        )}>
          {/* Core light line */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent opacity-100" />
          {/* Inner glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent blur-xs opacity-90" />
          {/* Medium glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent blur-md opacity-85" />
          {/* Outer glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent blur-lg opacity-80" />
          {/* Atmospheric glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent blur-2xl opacity-70 animate-pulse" />
          {/* Extra wide glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/90 to-transparent blur-3xl opacity-60" />
        </div>

        {/* Extended light reflection below bar */}
        <div className={cn(
          "absolute -bottom-16 left-0 right-0 h-16 pointer-events-none transition-opacity duration-700",
          showGlow ? "opacity-100" : "opacity-0"
        )}>
          <div className="absolute inset-0 bg-gradient-to-b from-primary/50 via-primary/20 to-transparent blur-3xl opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/15 to-transparent blur-2xl opacity-70 animate-pulse animation-delay-500" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/30 via-primary/10 to-transparent blur-xl opacity-60" />
        </div>
      </header>

      <BookingDialog open={showBooking} onOpenChange={setShowBooking} />
    </>
  );
};

export default Header;
